'use client'

import { useContext, useState, useCallback, useEffect, useMemo } from 'react'
import {
  Table,
  TableHeader,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@/components/ui/shadcn/table'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/shadcn/tooltip'
import { Alert, AlertDescription } from '@/components/ui/shadcn/alert'
import { Info } from 'lucide-react'
import { Button } from '@/components/ui/shadcn/button'
import { Badge } from '@/components/ui/shadcn/badge'
import { Input } from '@/components/ui/shadcn/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/shadcn/select'
import {
  Trash2 as DeleteIcon,
  Plus as AddIcon,
  RefreshCw as RefreshIcon,
  Info as InfoIcon,
  Pencil as EditIcon,
  Search as SearchIcon,
  X as ClearIcon,
} from 'lucide-react'
import { AuthContext } from '@/contexts/AuthContext'
import { ManagerContext } from '@/contexts/ManagerContext'
import MemberModal from '@/components/common/modals/MemberModal'
import ConfirmDelete from '@/components/common/modals/ConfirmDelete'
import { SettingsContext } from '@/contexts/SettingsContext'
import UserGroupChip from '@/components/common/UserGroupChip'

const ITEMS_PER_PAGE = 10

export default function Members({ billingDetails }) {
  const authContext = useContext(AuthContext)
  const context = useContext(ManagerContext)
  const { organization } = authContext.user || {}
  const [showInvite, setShowInvite] = useState(false)
  const [selectedMember, setSelectedMember] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [showDelete, setShowDelete] = useState(false)
  const settingsContext = useContext(SettingsContext)

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [userGroupFilter, setUserGroupFilter] = useState('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [sortField, setSortField] = useState('name')
  const [sortDirection, setSortDirection] = useState('asc')

  const handleDelete = () => {
    context.removeMemberInOrganization(organization.id, deleting.id, () => {
      setDeleting(null)
    })
  }

  const getUserGroup = useCallback(
    id => {
      return settingsContext.userGroups?.find(group => group.id === id) || null
    },
    [settingsContext.userGroups],
  )

  const getUserGroupName = useCallback(
    id => {
      const group = getUserGroup(id)
      return group?.name || null
    },
    [getUserGroup],
  )

  useEffect(() => {
    if (settingsContext.userGroups.length === 0) return

    const newMembers = []
    for (const member of organization.members) {
      const newMember = { ...member }

      if (member.memberId === organization.owner) {
        newMembers.push(newMember)
        continue
      }

      if (
        !settingsContext.userGroups.some(group => group.id === member.userGroup)
      ) {
        newMember.userGroup = null
      }
      newMembers.push(newMember)
    }

    if (JSON.stringify(newMembers) === JSON.stringify(organization.members))
      return

    authContext.setuser(
      {
        ...authContext.user,
        organization: {
          ...authContext.user.organization,
          members: newMembers,
        },
      },
      () => {},
    )
  }, [settingsContext, organization, authContext])

  const filteredMembers = useMemo(() => {
    let filtered = [...(organization?.members || [])]

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase()
      filtered = filtered.filter(
        member =>
          (member.name && member.name.toLowerCase().includes(term)) ||
          (member.email && member.email.toLowerCase().includes(term)),
      )
    }

    if (statusFilter !== 'all') {
      switch (statusFilter) {
        case 'owner':
          filtered = filtered.filter(
            member => member.memberId === organization.owner,
          )
          break
        case 'active':
          filtered = filtered.filter(
            member => member.name && member.memberId !== organization.owner,
          )
          break
        case 'pending':
          filtered = filtered.filter(
            member => !member.name && member.memberId !== organization.owner,
          )
          break
      }
    }

    if (userGroupFilter !== 'all') {
      if (userGroupFilter === 'none') {
        filtered = filtered.filter(
          member => !member.userGroup && member.memberId !== organization.owner,
        )
      } else {
        filtered = filtered.filter(
          member => member.userGroup === userGroupFilter,
        )
      }
    }

    filtered.sort((a, b) => {
      let aValue, bValue

      switch (sortField) {
        case 'name':
          aValue = (a.name || a.email || '').toLowerCase()
          bValue = (b.name || b.email || '').toLowerCase()
          break
        case 'email':
          aValue = (a.email || '').toLowerCase()
          bValue = (b.email || '').toLowerCase()
          break
        case 'userGroup':
          aValue = a.userGroup ? getUserGroupName(a.userGroup) || '' : ''
          bValue = b.userGroup ? getUserGroupName(b.userGroup) || '' : ''
          break
        default:
          aValue = ''
          bValue = ''
      }

      const comparison = aValue.localeCompare(bValue)
      return sortDirection === 'asc' ? comparison : -comparison
    })

    return filtered
  }, [
    organization?.members,
    searchTerm,
    statusFilter,
    userGroupFilter,
    sortField,
    sortDirection,
    getUserGroupName,
  ])

  const totalPages = Math.ceil(filteredMembers.length / ITEMS_PER_PAGE)
  const paginatedMembers = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
    return filteredMembers.slice(startIndex, startIndex + ITEMS_PER_PAGE)
  }, [filteredMembers, currentPage])

  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, statusFilter, userGroupFilter])

  const handleSort = field => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const clearFilters = () => {
    setSearchTerm('')
    setStatusFilter('all')
    setUserGroupFilter('all')
    setCurrentPage(1)
  }

  const hasActiveFilters =
    searchTerm || statusFilter !== 'all' || userGroupFilter !== 'all'

  if (!authContext.user?.isAuthorized('viewUsers')) {
    return null
  }

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="grid gap-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold">
              Members{' '}
              <Badge variant="outline" className="ml-1">
                {filteredMembers.length} of {organization?.members.length || 0}
              </Badge>
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Manage members of your organization (
              {organization?.members.length || 0} of {billingDetails?.teamSize}{' '}
              seats used)
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => context.refreshOrganization(organization.id)}
            >
              <RefreshIcon className="size-4 mr-1" />
              Refresh
            </Button>
            <Button
              onClick={() => setShowInvite(true)}
              disabled={
                !authContext.user?.isAuthorized('inviteUsers') ||
                organization?.members.length >= billingDetails?.teamSize
              }
            >
              <AddIcon className="size-4 mr-1" />
              Invite Member
            </Button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="rounded-lg border bg-muted/50 p-4">
          <div className="flex gap-4 items-center flex-wrap">
            <div className="flex-1 min-w-[200px] relative">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <Input
                className="pl-9 pr-9"
                placeholder="Search members..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                  onClick={() => setSearchTerm('')}
                >
                  <ClearIcon className="size-4 text-muted-foreground" />
                </button>
              )}
            </div>

            <div className="w-40">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Members</SelectItem>
                  <SelectItem value="owner">Owner</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="w-40">
              <Select
                value={userGroupFilter}
                onValueChange={setUserGroupFilter}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Group" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Groups</SelectItem>
                  <SelectItem value="none">No Group</SelectItem>
                  {settingsContext.userGroups.map(group => (
                    <SelectItem key={group.id} value={group.id}>
                      {group.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {hasActiveFilters && (
              <Button variant="outline" size="sm" onClick={clearFilters}>
                <ClearIcon className="size-4 mr-1" />
                Clear Filters
              </Button>
            )}
          </div>
        </div>

        {/* Results Summary */}
        {hasActiveFilters && (
          <Alert className="mb-2">
            <Info className="size-4" />
            <AlertDescription>
              Showing {filteredMembers.length} of{' '}
              {organization?.members.length || 0} members
              {searchTerm && ` matching "${searchTerm}"`}
            </AlertDescription>
          </Alert>
        )}

        {/* Members Table */}
        <TooltipProvider>
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead
                    className="cursor-pointer"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center">
                      Name/Email
                      {sortField === 'name' && (
                        <span className="text-xs ml-1">
                          {sortDirection === 'asc' ? '\u2191' : '\u2193'}
                        </span>
                      )}
                    </div>
                  </TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead
                    className="cursor-pointer"
                    onClick={() => handleSort('userGroup')}
                  >
                    <div className="flex items-center">
                      User Group
                      {sortField === 'userGroup' && (
                        <span className="text-xs ml-1">
                          {sortDirection === 'asc' ? '\u2191' : '\u2193'}
                        </span>
                      )}
                    </div>
                  </TableHead>
                  <TableHead className="w-[15%] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedMembers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="py-6 text-center">
                      <p className="text-muted-foreground">
                        {hasActiveFilters
                          ? 'No members match your search criteria.'
                          : 'No members found.'}
                      </p>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedMembers.map(member => (
                    <TableRow key={member.id}>
                      <TableCell className="whitespace-normal break-words align-top">
                        <p
                          className={`text-sm ${member.name ? 'font-medium' : ''}`}
                        >
                          {member.name || member.email}
                        </p>
                        {member.name && member.email && (
                          <span className="text-xs text-muted-foreground">
                            {member.email}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {member.memberId === organization.owner ? (
                            <Badge>Owner</Badge>
                          ) : member.name ? (
                            <Badge
                              variant="outline"
                              className="border-emerald-300 text-emerald-700"
                            >
                              Active
                            </Badge>
                          ) : (
                            <Badge
                              variant="outline"
                              className="border-amber-300 text-amber-700"
                            >
                              Pending
                            </Badge>
                          )}
                          {!member.name && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <InfoIcon className="size-4 text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent>
                                This person hasn&apos;t signed up yet
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {member.userGroup &&
                        member.memberId !== organization.owner ? (
                          <UserGroupChip
                            userGroup={getUserGroup(member.userGroup)}
                            variant="filled"
                          />
                        ) : member.memberId !== organization.owner ? (
                          <span className="text-xs text-muted-foreground">
                            No group assigned
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span>
                                <Button
                                  variant="ghost"
                                  size="icon-sm"
                                  aria-label="Edit member"
                                  disabled={
                                    member.memberId === organization.owner ||
                                    !authContext.user?.isAuthorized('editUsers')
                                  }
                                  onClick={() => {
                                    setSelectedMember(member)
                                    setShowInvite(true)
                                  }}
                                >
                                  <EditIcon className="size-4" />
                                </Button>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              {member.memberId === organization.owner
                                ? 'Cannot edit owner'
                                : 'Edit member'}
                            </TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span>
                                <Button
                                  variant="ghost"
                                  size="icon-sm"
                                  aria-label="Remove member"
                                  className="text-destructive hover:text-destructive"
                                  disabled={
                                    member.memberId === organization.owner ||
                                    !authContext.user?.isAuthorized(
                                      'deleteUsers',
                                    )
                                  }
                                  onClick={() => {
                                    setDeleting(member)
                                    setShowDelete(true)
                                  }}
                                >
                                  <DeleteIcon className="size-4" />
                                </Button>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              {member.memberId === organization.owner
                                ? 'Cannot remove owner'
                                : 'Remove member'}
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TooltipProvider>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-4 gap-1">
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(1)}
            >
              First
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            >
              Prev
            </Button>
            <span className="flex items-center px-3 text-sm">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            >
              Next
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(totalPages)}
            >
              Last
            </Button>
          </div>
        )}
      </div>
      <MemberModal
        open={showInvite}
        setOpen={setShowInvite}
        organization={authContext.user.organization}
        member={selectedMember}
        setMember={setSelectedMember}
      />
      <ConfirmDelete
        open={showDelete}
        setOpen={() => setShowDelete(false)}
        title="Remove Member"
        description={
          <>
            <div>
              <p>
                Removing a member from this list will remove all access this
                user has to the organization including read access.
              </p>
            </div>
            <div>
              <p>
                To confirm the removal of this member type their{' '}
                {deleting?.name ? 'name' : 'email'}:{' '}
                <b>{deleting?.name || deleting?.email}</b>
              </p>
            </div>
          </>
        }
        buttonText="Remove Member"
        fieldLabel={deleting?.name ? 'Member Name' : 'Member Email'}
        fieldType={deleting?.name ? 'text' : 'email'}
        reference={deleting?.name || deleting?.email}
        apiPath={`/api/admin/organization/${organization.id}/member/${deleting?.id}`}
        onComplete={handleDelete}
        onCancel={() => setDeleting(null)}
      />
    </div>
  )
}
